import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AdminRole, ChannelSettlementStatus, DEFAULT_PAGE_SIZE, trimDecimalZeros } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { StatusBadge } from "@/components/status-badge";
import { Amount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { HashLink } from "@/components/hash-link";
import { CHANNEL_PAYMENT_STATUS_COLOR, CHANNEL_SETTLEMENT_STATUS_COLOR, TRANSACTION_STATUS_COLOR } from "@/lib/status-colors";
import { formatStatusLabel } from "@/lib/utils";
import { hasRole } from "@/lib/roles";
import { ApiError } from "@/api/client";
import { getChannelSettlement, listChannelPayments, retryChannelSettlement } from "@/api/channels";
import { type ChannelPaymentListItem, type ChannelPaymentTransactionItem } from "@/api/types";

// Task 14 (feature-031 Phase B): channel settlement detail page, with the
// CHANNELS_MANAGER-only retry action — visible only on FAILED settlements,
// same "Retry settlement" placement/shape as
// _app.invoices_.$invoiceId.tsx's own retry button.
export const Route = createFileRoute("/_app/channel-settlements_/$channelSettlementId")({
  component: ChannelSettlementDetailPage,
});

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

/** Mirrors _app.channel-payments_.$channelPaymentId.tsx's own formatCrypto helper. */
function formatCrypto(amount: string | null, symbol: string): string {
  return amount === null ? "—" : `${trimDecimalZeros(amount)} ${symbol}`;
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </dd>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// ─── batched payments sub-table (settlementId filter, local pagination) ────

function BatchedPaymentsTable({ settlementId }: { settlementId: string }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isPending, isError } = useQuery({
    queryKey: ["channel-settlement", settlementId, "payments", page, pageSize],
    queryFn: () => listChannelPayments({ settlementId, page, pageSize }),
  });

  const columns: DataTableColumn<ChannelPaymentListItem>[] = [
    {
      key: "publicId",
      header: "Payment ID",
      cell: (p) => (
        <Link to="/channel-payments/$channelPaymentId" params={{ channelPaymentId: p.id }} className="group -m-2 block p-2">
          <span className="font-mono group-hover:underline">{p.publicId}</span>
        </Link>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (p) => (
        <StatusBadge label={formatStatusLabel(p.subStatus ?? p.status)} color={CHANNEL_PAYMENT_STATUS_COLOR[p.status]} />
      ),
    },
    {
      key: "amount",
      header: "Amount",
      cell: (p) => <Amount native={p.cryptoAmount} symbol={p.asset.symbol} usd={p.cryptoAmountUsd} />,
    },
    {
      key: "senderAddress",
      header: "Sender",
      cell: (p) => <HashLink value={p.senderAddress} type="address" network={p.asset.network} className="text-xs" />,
    },
    { key: "createdAt", header: "Created", cell: (p) => formatTimestamp(p.createdAt) },
  ];

  return (
    <DataTable
      searchable={false}
      columns={columns}
      items={data?.data ?? []}
      total={data?.total ?? 0}
      page={page}
      pageSize={pageSize}
      loading={isPending}
      error={isError ? "Failed to load payments." : null}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setPage(1);
      }}
      getRowKey={(p) => p.id}
      emptyMessage="No payments in this batch."
    />
  );
}

function ChannelSettlementDetailPage() {
  const { channelSettlementId } = Route.useParams();
  const { admin } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const canRetry = hasRole(admin.roles, AdminRole.CHANNELS_MANAGER);

  const {
    data: settlement,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["channel-settlement", channelSettlementId],
    queryFn: () => getChannelSettlement(channelSettlementId),
  });

  const retryMutation = useMutation({
    mutationFn: () => retryChannelSettlement(settlement!.publicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["channel-settlement", channelSettlementId] });
      toast.success("Settlement retried.");
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Failed to retry this settlement.");
    },
  });

  if (isPending) {
    return <div className="py-10 text-center text-sm text-muted-foreground">Loading settlement…</div>;
  }
  if (isError || !settlement) {
    return <div className="py-10 text-center text-sm text-muted-foreground">Failed to load settlement.</div>;
  }

  const showRetryButton = canRetry && settlement.status === ChannelSettlementStatus.FAILED;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{settlement.publicId}</h2>
          <p className="text-sm text-muted-foreground">
            {settlement.merchant.businessName} · {settlement.merchant.email}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge
            label={formatStatusLabel(settlement.status)}
            color={CHANNEL_SETTLEMENT_STATUS_COLOR[settlement.status]}
          />
          {showRetryButton && (
            <Button type="button" size="sm" disabled={retryMutation.isPending} onClick={() => retryMutation.mutate()}>
              {retryMutation.isPending ? "Retrying…" : "Retry settlement"}
            </Button>
          )}
        </div>
      </div>

      <SectionCard title="Summary">
        <dl className="space-y-2 text-sm">
          <DetailRow
            label="Channel"
            value={
              <Link to="/channels/$channelId" params={{ channelId: settlement.channel.id }} className="hover:underline">
                {settlement.channel.label}{" "}
                <span className="font-mono text-xs text-muted-foreground">({settlement.channel.publicId})</span>
              </Link>
            }
          />
        </dl>
        <div className="my-4 flex flex-wrap items-end justify-between gap-4 border-b pb-4">
          <Amount native={settlement.grossAmount} symbol={settlement.asset.symbol} />
          <AssetLabel symbol={settlement.asset.symbol} network={settlement.asset.network} inline />
        </div>
        <dl className="space-y-2 text-sm">
          <DetailRow label="Payments in batch" value={settlement.paymentCount} />
          <DetailRow label="Created" value={formatTimestamp(settlement.createdAt)} />
        </dl>
      </SectionCard>

      <SectionCard title="Transaction">
        {settlement.transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground">Not settled yet.</p>
        ) : (
          <div className="overflow-hidden overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted text-left font-semibold text-muted-foreground">
                  <th className="px-3 py-2">Operation</th>
                  <th className="px-3 py-2">Tx hash</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {settlement.transactions.map((tx: ChannelPaymentTransactionItem) => (
                  <tr key={tx.id} className="border-t">
                    <td className="px-3 py-2 font-medium">{formatStatusLabel(tx.operation)}</td>
                    <td className="px-3 py-2">
                      <HashLink value={tx.txHash} type="tx" network={tx.asset.network} />
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge label={formatStatusLabel(tx.status)} color={TRANSACTION_STATUS_COLOR[tx.status]} />
                    </td>
                    <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatCrypto(tx.amount, tx.asset.symbol)}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{formatTimestamp(tx.chainStartedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Batched Payments">
        <BatchedPaymentsTable settlementId={settlement.id} />
      </SectionCard>
    </div>
  );
}
